/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useRef, useState, useEffect} from "react";

import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";

import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";


import {Switch, Route, Redirect} from "react-router-dom";



import {useDispatch} from "react-redux";
import ApplicationApiContext from "@Contexts/Api";
import {ERROR_SYSTEM_MESSAGE_INTERFACE} from "@Services/Application/Interfaces/_Messages/Errors";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";
import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";
import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";
import {TERMINAL_INTERFACE} from "@Services/Application/Interfaces/Terminals/Terminal";
import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";
import WarningMessageComponent from "@Components/UI/Communicates/Messages/Warning";
import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";

import {Col, Row} from "react-bootstrap";
import TerminalMonitoryNoteComponent from "@UIPages/Authenticated/MonitoryNotes/Terminals/Terminal/MonitoryNote";
import DELETE_MONITORY_NOTE_REQUEST_HANDLER from "@Services/Application/Requests/MonitoryNotes/Delete";
import MONITORY_NOTE_INTERFACE from "@Services/Application/Interfaces/MonitoryNotes/MonitoryNote";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import CHANGE_API_REQUEST_TOKEN_ACTION from "@Actions/Api/ChangeApiRequestTokenValue";
import prepareRestApiRequestToken from "@Services/Utilities/RequestToken/Prepare";
import ContentInformationComponent from "@Components/UI/Content/Information";


type MonitoryNotesTerminalProps = {
    location: any;
    match: any;
};


export const MonitoryNotesTerminalComponent: React.FC<MonitoryNotesTerminalProps> = ({
    location, match
}): JSX.Element | null => {


    const TERMINAL_ID: string | null = match?.params?.terminalId || null;
    const TERMINAL_NAME: string | null = location?.state?.terminalName || null;


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {React.Ref} My Favourite Terminals Page Container DOM Ref */
    const COMPONENT_REF: React.Ref<HTMLDivElement | null> = useRef(null);


    /** @const {UserInterface | null} LOGGED_USER - Application Global State Logged User Object */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;


    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;



    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    const [MONITORY_NOTES, SET_MONITORY_NOTES] = useState<[] | null>(null);
    const [MONITORY_NOTE_DELETE_STATUS, SET_MONITORY_NOTE_DELETE_STATUS] = useState<boolean>(false);
    const [MONITORY_NOTES_MESSAGE, SET_MONITORY_NOTES_MESSAGE] = useState<string | null>(null);
    const [MONITORY_NOTES_ERROR, SET_MONITORY_NOTES_ERROR] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);


    useEffect(() => {

        if (!MONITORY_NOTE_DELETE_STATUS && MONITORY_NOTES !== null) {
            return;
        } else {
            SET_MONITORY_NOTE_DELETE_STATUS(false);
        }

        if (!TERMINAL_ID) return;

        if (MONITORY_NOTES) return;

        const MONITORY_NOTES_API_URL: string = REST_API_URL + "/TransportNotification/List";

        const MONITORY_NOTES_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID, // Logged User ID,
            TerminalNo: TERMINAL_ID,
            Today: false
        };

        const GET_MONITORY_NOTES = async (): Promise<void> => {

            try {

                const MONITORY_NOTES_RESPONSE = await REST_API_REQUEST(
                    MONITORY_NOTES_API_URL,
                    MONITORY_NOTES_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN
                );

                if (MONITORY_NOTES_RESPONSE.status === 200) {

                    const CONTENT: string = await MONITORY_NOTES_RESPONSE.text();
                    SET_MONITORY_NOTES(CONTENT?.trim() ? JSON.parse(CONTENT)?.TransportNotifications : []);

                } else {
                    const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await MONITORY_NOTES_RESPONSE.json();
                    SET_MONITORY_NOTES_ERROR({ErrorCode: MONITORY_NOTES_RESPONSE.status, UserMessage: CONTENT.UserMessage});
                }

            } catch (e) {
                SET_MONITORY_NOTES_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

        };

        GET_MONITORY_NOTES();

    }, [REST_API_URL, MONITORY_NOTE_DELETE_STATUS]);

    useEffect(() => {
        return () => {
            SET_MONITORY_NOTES(null);
            SET_MONITORY_NOTES_ERROR(null);
            SET_MONITORY_NOTES_MESSAGE(null);
            SET_MONITORY_NOTE_DELETE_STATUS(false);
        };
    }, []);


    if (!ROUTER) return <Redirect to={"/404"}/>;


    if (!TERMINAL_ID) return <Redirect to={"/404"} />;


    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={ROUTER.NOT_AUTHENTICATED.LOGIN.URL}/>;


    if (MONITORY_NOTES_ERROR) return <ErrorMessageComponent messageText={MONITORY_NOTES_ERROR.UserMessage} />;


    if (!MONITORY_NOTES) return null;


    const MONITORY_NOTE_DELETE_HANDLER = async (monitoryNoteId: string) => {

        try {

            const DELETE_RESPONSE = await DELETE_MONITORY_NOTE_REQUEST_HANDLER(
                `${REST_API_URL}/TransportNotification`,
                monitoryNoteId,
                LOGGED_USER?.userID
            );

            if (DELETE_RESPONSE?.success) {
                SET_MONITORY_NOTES_MESSAGE("Awizacja została usunięta");
                SET_MONITORY_NOTE_DELETE_STATUS(true);
            } else {
                SET_MONITORY_NOTES_ERROR(DELETE_RESPONSE);
            }

        } catch (e) {
            SET_MONITORY_NOTES_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
        }

    };


    MONITORY_NOTES?.sort(
        (monitoryNote1: MONITORY_NOTE_INTERFACE, monitoryNote2: MONITORY_NOTE_INTERFACE) => {
            return new Date(monitoryNote1?.ProcessStartDate || "") > new Date(monitoryNote2?.ProcessStartDate || "") ? 1 : -1;
        }
    );


    const MonitoryNotesList = MONITORY_NOTES.map((monitoryNote: MONITORY_NOTE_INTERFACE, i: number) =>
        <TerminalMonitoryNoteComponent
            key={monitoryNote?.TransportNotificationNo}
            MONITORY_NOTE={monitoryNote}
            monitoryNoteDeleteHandler={MONITORY_NOTE_DELETE_HANDLER}
            MONITORY_NOTE_INDEX={i}
        />
    );


    return (
        <BlockContentContainerComponent forwardRef={COMPONENT_REF} cssClasses={"monitory-notes-terminal-page-component"}>

            <BlockContentHeaderComponent headerText={`Moje Awizacje`} />

            <Col xs={12} className={"monitory-notes-list monitory-notes-list-component"}>
                <Row className={"monitory-notes-list-content"}>

                    {!!MonitoryNotesList && MonitoryNotesList.length > 0 ?

                    MonitoryNotesList
                    :

                    <ContentInformationComponent contentMessage={`Nie masz żadnych Awizacji`} />

                    }

                </Row>
            </Col>

        </BlockContentContainerComponent>
    );

};


export default MonitoryNotesTerminalComponent;
